import Permissions, { usePermissions } from '@app/src/auth/permissions'
import ParentRowOrFragment from '@app/src/components/Skeleton/ParentRowOrFragment'
import AnswerWithClassificationCell from '@app/src/components/Table/Cells/AnswerWithClassificationCell'
import AnswerWithFlagCell from '@app/src/components/Table/Cells/AnswerWithFlagCell'
import CommentCell from '@app/src/components/Table/Cells/CommentCell'
import DateCell from '@app/src/components/Table/Cells/DateCell'
import ExternalVerificationStatusCell from '@app/src/components/Table/Cells/ExternalVerificationStatusCell'
import LinkCell from '@app/src/components/Table/Cells/LinkCell'
import PeriodNameCell from '@app/src/components/Table/Cells/PeriodNameCell'
import RequestCell from '@app/src/components/Table/Cells/RequestCell'
import StatusCell from '@app/src/components/Table/Cells/StatusCell'
import ValueCell from '@app/src/components/Table/Cells/ValueCell'
import type { ResponseItem } from '@app/src/types/resourceExplorer'
import { ExternalVerificationStatus } from '@app/src/types/resourceExplorer'
import paths, { reporting } from '@app/src/wf-constants/paths'
import markdownToTxt from 'markdown-to-txt'
import qs from 'qs'
import React from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router'
import { generatePath } from 'react-router-dom'

export interface ResponseItemRowProps {
  row: ResponseItem
  disableTableRow?: boolean
  showAnswerClassificationResults?: boolean
  showTargetProductColumn?: boolean
}

export const ResponseItemRow = ({
  row: responseItem,
  disableTableRow,
  showAnswerClassificationResults,
  showTargetProductColumn,
}: ResponseItemRowProps): JSX.Element => {
  const { hasPermission } = usePermissions()
  const history = useHistory()
  const verificationPermitted = hasPermission(Permissions.WRITE_VERIFICATION)
  const { formatMessage } = useIntl()

  return (
    <ParentRowOrFragment disableTableRow={disableTableRow}>
      {showTargetProductColumn &&
        (responseItem.response.request.targetProduct ? (
          <LinkCell
            label={responseItem.response.request.targetProduct?.name}
            to={generatePath(paths.product, {
              id: responseItem.response.request.targetProduct?.id,
            })}
          />
        ) : (
          <ValueCell />
        ))}
      <ValueCell value={responseItem.requestItem.description} />
      {showAnswerClassificationResults ? (
        <AnswerWithClassificationCell item={responseItem} />
      ) : (
        <AnswerWithFlagCell item={responseItem} />
      )}
      <CommentCell responseItem={responseItem} />
      <PeriodNameCell periodName={responseItem.response.request.periodName} />
      <RequestCell
        title={responseItem.response.request.title}
        imageUrl={responseItem.response.request.template?.image?.url}
        onClick={() =>
          history.push({
            pathname: reporting,
            search: qs.stringify({ requestId: responseItem.response.request.id, responseId: responseItem.response.id }),
          })
        }
      />
      {verificationPermitted && (
        <>
          <ExternalVerificationStatusCell
            responseStatus={
              responseItem?.verifications?.[0]?.externalVerificationStatus ?? ExternalVerificationStatus.NotSet
            }
            draftStatus={responseItem?.response?.draftStatus}
          />
          <ValueCell value={markdownToTxt(responseItem?.verifications?.[0]?.externalVerificationComment || '')} />
          <StatusCell verification value={responseItem?.verifications?.[0]?.internalVerificationStatus} />
          <ValueCell value={markdownToTxt(responseItem?.verifications?.[0]?.internalVerificationComment || '')} />
        </>
      )}
      <DateCell value={responseItem.response.submittedAt} />
      <ValueCell
        value={
          responseItem?.isScraped
            ? formatMessage({ id: 'general.aiScreening' })
            : formatMessage({ id: 'general.selfReported' })
        }
      />
    </ParentRowOrFragment>
  )
}

export default ResponseItemRow
